import React, { useState } from "react";
import { useAuth, formatError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AuthModal({ open, onOpenChange, mode: initMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // eslint-disable-next-line
  React.useEffect(() => { setMode(initMode); setErr(""); }, [initMode, open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(email, password, name || email.split("@")[0]);
        toast.success("Account created!");
      }
      onOpenChange(false);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </DialogTitle>
          <DialogDescription>
            {mode === "login"
              ? "Log in to save guides and pick up where you left off."
              : "Save your progress, bookmark guides, and get personalized help."}
          </DialogDescription>
        </DialogHeader>

        <Button
          type="button"
          variant="outline"
          onClick={googleSignIn}
          data-testid="google-signin-btn"
          className="w-full flex items-center justify-center gap-2 h-11"
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </Button>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-slate-500">or with email</span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label htmlFor="auth-name">Name</Label>
              <Input id="auth-name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
            </div>
          )}
          <div>
            <Label htmlFor="auth-email">Email</Label>
            <Input id="auth-email" data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="auth-password">Password</Label>
            <Input id="auth-password" data-testid="auth-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
          </div>

          {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}

          <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full h-11 bg-[#0033A0] hover:bg-[#002277]">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-slate-600 text-center">
          {mode === "login" ? (
            <>New here?{" "}
              <button type="button" onClick={() => setMode("register")} data-testid="switch-to-register" className="text-[#0033A0] font-semibold hover:underline">
                Create an account
              </button>
            </>
          ) : (
            <>Already have one?{" "}
              <button type="button" onClick={() => setMode("login")} data-testid="switch-to-login" className="text-[#0033A0] font-semibold hover:underline">
                Log in
              </button>
            </>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
